import React from "react";
import { C1_TYPES } from "../../../data/riskAssessmentData";
import {
    formatABN,
    formatACN,
    type ABNLookupResult,
    type ACNLookupResult,
} from "../../../services/abnLookupService";
import { type EntityCodeType } from "../../../services/registrationApi";
import type { C1TypeId, C2TypeId } from "../../../types/risk-assessment.types";
import { S, primary } from "./styles";
import type { EntitySubType } from "./useRiskAssessmentState";

interface StepOneProps {
  c1Type: C1TypeId | null;
  setC1Type: (v: C1TypeId) => void;
  c2Name: string;
  setC2Name: (v: string) => void;
  c2EntityType: C2TypeId | null;
  setC2EntityType: (v: C2TypeId | null) => void;
  isC2Individual: boolean | null;
  setIsC2Individual: (v: boolean | null) => void;
  entitySubType: EntitySubType;
  setEntitySubType: (v: EntitySubType) => void;
  c2Abn: string;
  setC2Abn: (v: string) => void;
  abnCodeType: EntityCodeType;
  abnLookupResult: ABNLookupResult | ACNLookupResult | null;
  abnLookupError: string;
  isLookingUp: boolean;
  handleAbnChange: (value: string) => void;
  handleAbnCodeTypeChange: (type: EntityCodeType) => void;
  triggerKybLookup: (code: string, type: EntityCodeType) => void;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setAbnLookupResult: (v: ABNLookupResult | ACNLookupResult | null) => void;
  setAbnLookupError: (v: string) => void;
  setStep: (s: number) => void;
  contactFirst: string;
  setContactFirst: (v: string) => void;
  contactMiddle: string;
  setContactMiddle: (v: string) => void;
  contactLast: string;
  setContactLast: (v: string) => void;
  contactEmail: string;
  setContactEmail: (v: string) => void;
  contactPhone: string;
  setContactPhone: (v: string) => void;
}

const StepOne: React.FC<StepOneProps> = ({
  c1Type,
  setC1Type,
  c2Name,
  setC2Name,
  c2EntityType,
  setC2EntityType,
  isC2Individual,
  setIsC2Individual,
  entitySubType,
  setEntitySubType,
  c2Abn,
  setC2Abn,
  abnCodeType,
  abnLookupResult,
  abnLookupError,
  isLookingUp,
  handleAbnChange,
  handleAbnCodeTypeChange,
  triggerKybLookup,
  setAnswers,
  setAbnLookupResult,
  setAbnLookupError,
  setStep,
  contactFirst,
  setContactFirst,
  contactMiddle,
  setContactMiddle,
  contactLast,
  setContactLast,
  contactEmail,
  setContactEmail,
  contactPhone,
  setContactPhone,
}) => (
  <div style={S.body}>
    {/* C1 Organisation (read-only from profile) */}
    <div style={{ marginBottom: 22 }}>
      <label style={S.lbl}>C1 — Your Organisation (Reporting Entity)</label>
      {c1Type ? (
        (() => {
          const c1 = C1_TYPES.find((t) => t.id === c1Type);
          return c1 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "14px 18px",
                background: "#EFF6FF",
                border: "1.5px solid #BFDBFE",
                borderRadius: 12,
              }}
            >
              <span style={{ fontSize: 28 }}>{c1.icon}</span>
              <div>
                <div
                  style={{
                    color: "#1B3A6B",
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  {c1.label}
                </div>
                <div
                  style={{
                    color: "#64748B",
                    fontSize: 11,
                    marginTop: 1,
                  }}
                >
                  {c1.desc}
                </div>
              </div>
              <div
                style={{
                  marginLeft: "auto",
                  background: "#DBEAFE",
                  borderRadius: 6,
                  padding: "3px 10px",
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#1E40AF",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                From Profile
              </div>
            </div>
          ) : null;
        })()
      ) : (
        <div>
          <div
            style={{
              marginBottom: 10,
              color: "#92400E",
              fontSize: 12,
              fontWeight: 600,
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              borderRadius: 8,
              padding: "8px 14px",
            }}
          >
            ⚠ Organisation type not auto-detected — please select below.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {C1_TYPES.map((t) => (
              <div
                key={t.id}
                onClick={() => setC1Type(t.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 14px",
                  background: "#F8FAFC",
                  border: "1.5px solid #E2E8F0",
                  borderRadius: 10,
                  cursor: "pointer",
                }}
              >
                <span style={{ fontSize: 22 }}>{t.icon}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1B3A6B" }}>{t.label}</div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

    <div style={{ marginBottom: 20 }}>
      <label style={S.lbl}>C2 — Customer Type</label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        {/* Individual card */}
        <div
          onClick={() => {
            if (isC2Individual !== true) {
              // Only reset when actually switching from Business/null to Individual
              setC2Name("");
              setAbnLookupResult(null);
              setAbnLookupError("");
              setC2Abn("");
            }
            setIsC2Individual(true);
            setEntitySubType("");
            setC2EntityType("individual");
            setAnswers({});
          }}
          style={{
            border: `2px solid ${isC2Individual === true ? "#2563eb" : "#e5e7eb"}`,
            borderRadius: 12,
            padding: "20px 16px",
            cursor: "pointer",
            background: isC2Individual === true ? "#eff6ff" : "#fff",
            transition: "all 0.15s",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 32, marginBottom: 8 }}>👤</div>
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              color: "#111",
              marginBottom: 4,
            }}
          >
            Individual
          </div>
          <div style={{ fontSize: 12, color: "#666" }}>
            Natural person — no ABN/ACN required
          </div>
        </div>

        {/* Business card */}
        <div
          onClick={() => {
            if (isC2Individual !== false) {
              // Only reset when actually switching from Individual/null to Business
              setC2Name("");
              setC2EntityType(null);
            }
            setIsC2Individual(false);
            setEntitySubType("");
            setAnswers({});
          }}
          style={{
            border: `2px solid ${isC2Individual === false ? "#2563eb" : "#e5e7eb"}`,
            borderRadius: 12,
            padding: "20px 16px",
            cursor: "pointer",
            background: isC2Individual === false ? "#eff6ff" : "#fff",
            transition: "all 0.15s",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 32, marginBottom: 8 }}>🏢</div>
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              color: "#111",
              marginBottom: 4,
            }}
          >
            Business / Organisation
          </div>
          <div style={{ fontSize: 12, color: "#666" }}>
            Company, Trust, Partnership, etc.
          </div>
        </div>
      </div>
    </div>

    {/* ── ABN / ACN lookup (business entities only) ── */}
    {isC2Individual === false && (
      <div style={{ marginBottom: 20 }}>
        <label style={S.lbl}>ABN or ACN *</label>
        <div
          style={{
            display: "flex",
            gap: 0,
            alignItems: "stretch",
          }}
        >
          <input
            value={c2Abn}
            onChange={(e) => {
              const value = e.target.value;
              handleAbnChange(value);
              // Auto-detect ABN vs ACN based on digit count
              const digits = value.replace(/\D/g, "");
              if (digits.length === 9 && abnCodeType !== "ACN") {
                handleAbnCodeTypeChange("ACN");
              } else if (digits.length === 11 && abnCodeType !== "ABN") {
                handleAbnCodeTypeChange("ABN");
              } else if (digits.length < 9 && abnCodeType !== "ABN") {
                // Default to ABN while typing
                handleAbnCodeTypeChange("ABN");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const digits = c2Abn.replace(/\D/g, "");
                if (digits.length === 9 || digits.length === 11) {
                  const type = digits.length === 9 ? "ACN" : "ABN";
                  triggerKybLookup(digits, type);
                }
              }
            }}
            placeholder="Enter ABN (11 digits) or ACN (9 digits)"
            style={{
              ...S.inp,
              flex: 1,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderRight: "none",
            }}
          />
          <button
            type="button"
            onClick={() => {
              const digits = c2Abn.replace(/\D/g, "");
              const type = digits.length === 9 ? "ACN" : "ABN";
              triggerKybLookup(digits, type);
            }}
            disabled={isLookingUp || (() => {
              const digits = c2Abn.replace(/\D/g, "");
              return digits.length !== 9 && digits.length !== 11;
            })()}
            style={{
              padding: "12px 24px",
              background: (() => {
                const digits = c2Abn.replace(/\D/g, "");
                const isValid = digits.length === 9 || digits.length === 11;
                return isLookingUp || !isValid ? "#e5e7eb" : "#1B3A6B";
              })(),
              color: (() => {
                const digits = c2Abn.replace(/\D/g, "");
                const isValid = digits.length === 9 || digits.length === 11;
                return isLookingUp || !isValid ? "#9ca3af" : "#fff";
              })(),
              border: "1px solid #CBD5E1",
              borderLeft: "none",
              borderTopRightRadius: 10,
              borderBottomRightRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: (() => {
                const digits = c2Abn.replace(/\D/g, "");
                const isValid = digits.length === 9 || digits.length === 11;
                return isLookingUp || !isValid ? "not-allowed" : "pointer";
              })(),
              whiteSpace: "nowrap",
              minWidth: 100,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {isLookingUp ? "Looking up…" : "LOOKUP"}
          </button>
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#1B3A6B",
            marginTop: 8,
            padding: "8px 12px",
            background: "#EFF6FF",
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ color: "#f59e0b" }}>ℹ</span>
          Enter an ABN (11 digits) or ACN (9 digits) and press Enter or click Lookup
        </div>

        {/* Lookup error */}
        {abnLookupError && (
          <div
            style={{
              padding: "8px 12px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              color: "#dc2626",
              fontSize: 13,
              marginTop: 8,
            }}
          >
            ❌ {abnLookupError}
          </div>
        )}

        {/* Loading indicator */}
        {isLookingUp && (
          <div
            style={{
              padding: "10px 14px",
              background: "#f0f7ff",
              border: "1px solid #bfdbfe",
              borderRadius: 8,
              color: "#1d4ed8",
              fontSize: 13,
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                animation: "spin 1s linear infinite",
                display: "inline-block",
              }}
            >
              ⟳
            </span>
            Looking up {abnCodeType}…
          </div>
        )}

        {/* Lookup result */}
        {abnLookupResult && !isLookingUp && (
          <div
            style={{
              padding: "12px 16px",
              background: abnLookupResult.isActive ? "#f0fdf4" : "#fffbeb",
              border: `1px solid ${abnLookupResult.isActive ? "#bbf7d0" : "#fde68a"}`,
              borderRadius: 8,
              marginTop: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <span style={{ fontSize: "1.2rem", marginTop: 1 }}>
                {abnLookupResult.isActive ? "✅" : "⚠️"}
              </span>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "#111",
                  }}
                >
                  {abnLookupResult.name}
                </span>
                {"abn" in abnLookupResult && abnLookupResult.abn && (
                  <span style={{ fontSize: 12, color: "#555" }}>
                    ABN: {formatABN(abnLookupResult.abn)}
                  </span>
                )}
                {"acn" in abnLookupResult && abnLookupResult.acn && (
                  <span style={{ fontSize: 12, color: "#555" }}>
                    ACN: {formatACN(abnLookupResult.acn)}
                  </span>
                )}
                {abnLookupResult.status && (
                  <span style={{ fontSize: 12, color: "#555" }}>
                    Status: {abnLookupResult.status}
                  </span>
                )}
                {abnLookupResult.businessType && (
                  <span style={{ fontSize: 12, color: "#555" }}>
                    Entity Type: {abnLookupResult.businessType}
                  </span>
                )}
                {(entitySubType || c2EntityType) && (
                  <span
                    style={{
                      display: "inline-block",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#1d4ed8",
                      background: "#dbeafe",
                      borderRadius: 6,
                      padding: "3px 10px",
                      marginTop: 2,
                    }}
                  >
                    Detected:{" "}
                    {entitySubType
                      ? entitySubType
                          .replace("-", " ")
                          .replace(/\b\w/g, (c: string) => c.toUpperCase())
                      : c2EntityType === "individual"
                        ? "Individual"
                        : c2EntityType}
                  </span>
                )}
                {abnLookupResult.address && (
                  <span style={{ fontSize: 12, color: "#555" }}>
                    Location: {abnLookupResult.address}
                  </span>
                )}
                <span
                  style={{
                    fontSize: 11,
                    color: "#16a34a",
                    fontWeight: 500,
                    marginTop: 2,
                  }}
                >
                  ✓ Entity name: {c2Name}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    )}

    {/* C2 Entity Name — only shown for individuals (manual entry) */}
    {isC2Individual === true && (
    <div style={{ marginBottom: 20 }}>
      <label style={S.lbl}>
        C2 — Customer Full Legal Name *
      </label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <input
          style={{
            ...S.inp,
            textTransform: "uppercase",
          }}
          placeholder="First name"
          value={contactFirst}
          onChange={(e) => {
            const first = e.target.value;
            setContactFirst(first);
            setC2Name([first, contactMiddle, contactLast].filter(Boolean).join(" "));
          }}
          onFocus={(e) => (e.target.style.borderColor = "#1B3A6B")}
          onBlur={(e) => (e.target.style.borderColor = "#CBD5E1")}
        />
        <input
          style={{
            ...S.inp,
            textTransform: "uppercase",
          }}
          placeholder="Middle (optional)"
          value={contactMiddle}
          onChange={(e) => {
            const middle = e.target.value;
            setContactMiddle(middle);
            setC2Name([contactFirst, middle, contactLast].filter(Boolean).join(" "));
          }}
          onFocus={(e) => (e.target.style.borderColor = "#1B3A6B")}
          onBlur={(e) => (e.target.style.borderColor = "#CBD5E1")}
        />
        <input
          style={{
            ...S.inp,
            textTransform: "uppercase",
          }}
          placeholder="Last name"
          value={contactLast}
          onChange={(e) => {
            const last = e.target.value;
            setContactLast(last);
            setC2Name([contactFirst, contactMiddle, last].filter(Boolean).join(" "));
          }}
          onFocus={(e) => (e.target.style.borderColor = "#1B3A6B")}
          onBlur={(e) => (e.target.style.borderColor = "#CBD5E1")}
        />
      </div>
      {contactFirst.includes(" ") && (
        <div style={{ marginTop: 6, padding: "6px 10px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 6, fontSize: 11, color: "#92400E", lineHeight: 1.4 }}>
          ⚠ First name contains a space. If &quot;{contactFirst.split(/\s+/).slice(1).join(" ")}&quot; is a middle name, please enter it in the Middle field.
        </div>
      )}
    </div>
    )}

    <button
      onClick={() => setStep(2)}
      disabled={!c1Type || !c2Name.trim() || !c2EntityType}
      style={primary(!!c1Type && !!c2Name.trim() && !!c2EntityType)}
    >
      Continue to Risk Questionnaire →
    </button>
  </div>
);

export default StepOne;
